'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect } from 'react';
import { ADITUDE_LOAD_SCRIPT_URL, GPT_SCRIPT_URL } from '../../../constants/dependency-urls';
import type { AdProviderSlotProps } from '../../../types/ad-slot-props';
import type { GptSlotRenderEndedEvent } from '../../../types/globals';
import { logAdLifecycle, logAdLifecycleWarn } from '../../../utils/ad-lifecycle-log';
import { claimSlotInit, ensureScriptLoaded, injectPreloadLink, releaseSlotInit } from '../../../utils/dependencies';
type AditudeSlotMetadata = {
  pageTargeting?: Record<string, string>;
};
export type AditudeSlotProps = AdProviderSlotProps<AditudeSlotMetadata>;
function ensureAditudeStubs(): void {
  window.googletag = window.googletag ?? {
    cmd: []
  };
  window.tude = window.tude ?? {
    cmd: []
  };
}
function refreshAditudeSlot({
  divId,
  slotId,
  metadata,
  emailHash
}: {
  divId: string;
  slotId: string;
  metadata?: AditudeSlotMetadata;
  emailHash: string | null;
}): void {
  const apply = (): void => {
    if (emailHash) {
      window.tude?.setIdProfile?.({
        e: emailHash
      });
    }
    window.tude?.setDeviceType?.('desktop');
    window.tude?.setPageTargeting?.(metadata?.pageTargeting ?? {});
    window.tude?.refreshAdsViaDivMappings?.([{
      divId,
      baseDivId: slotId
    }]);
    logAdLifecycle('aditude:slot:refresh', {
      divId,
      slotId
    });
  };
  if (typeof window.tude?.refreshAdsViaDivMappings === 'function') {
    apply();
    return;
  }
  window.tude?.cmd.push(apply);
}
export function AditudeSlot(t0) {
  const $ = _c(14);
  const {
    slotId,
    divId,
    metadata,
    emailHash,
    isReady,
    isServerRendered,
    onAdStart,
    cancelAdStart,
    trackInitialized,
    trackEmpty,
    startFillTimeouts,
    clearFillTimeouts
  } = t0;
  let t1;
  let t2;
  if ($[0] !== cancelAdStart || $[1] !== clearFillTimeouts || $[2] !== divId || $[3] !== emailHash || $[4] !== isReady || $[5] !== isServerRendered || $[6] !== metadata || $[7] !== onAdStart || $[8] !== slotId || $[9] !== startFillTimeouts || $[10] !== trackEmpty || $[11] !== trackInitialized) {
    t1 = () => {
      if (!isReady) {
        return;
      }
      let cancelled = false;
      let slotInitialized = false;
      let handleSlotRenderEnded = null;
      const initSlot = () => {
        if (cancelled || !claimSlotInit(divId)) {
          return;
        }
        if (!document.getElementById(divId)) {
          logAdLifecycleWarn("aditude:slot:missing-div", {
            divId,
            slotId
          });
          cancelAdStart();
          return;
        }
        slotInitialized = true;
        logAdLifecycle("aditude:slot:init", {
          divId,
          slotId,
          source: isServerRendered ? "server" : "client",
          metadata,
          emailHash
        });
        trackInitialized();
        startFillTimeouts();
        handleSlotRenderEnded = event => {
          if (event.slot?.getSlotElementId() !== divId) {
            return;
          }
          clearFillTimeouts();
          if (event.isEmpty) {
            logAdLifecycle("aditude:slot:render-empty", {
              divId,
              slotId
            });
            trackEmpty();
            cancelAdStart();
            return;
          }
          logAdLifecycle("aditude:slot:render-filled", {
            divId,
            slotId
          });
          onAdStart();
        };
        const listener = handleSlotRenderEnded;
        window.googletag?.cmd.push(() => {
          window.googletag?.pubads?.()?.addEventListener("slotRenderEnded", listener);
        });
        refreshAditudeSlot({
          divId,
          slotId,
          metadata,
          emailHash
        });
      };
      ensureAditudeStubs();
      if (isServerRendered) {
        initSlot();
      } else {
        injectPreloadLink(GPT_SCRIPT_URL);
        ensureScriptLoaded(ADITUDE_LOAD_SCRIPT_URL).then(() => {
          initSlot();
        }).catch(error => {
          logAdLifecycleWarn("aditude:scripts:failed", {
            divId,
            slotId,
            message: String(error)
          });
          cancelAdStart();
        });
      }
      return () => {
        cancelled = true;
        clearFillTimeouts();
        if (handleSlotRenderEnded) {
          const listener_0 = handleSlotRenderEnded;
          window.googletag?.cmd.push(() => {
            window.googletag?.pubads?.()?.removeEventListener("slotRenderEnded", listener_0);
          });
        }
        if (slotInitialized) {
          releaseSlotInit(divId);
        }
        cancelAdStart();
      };
    };
    t2 = [cancelAdStart, clearFillTimeouts, divId, emailHash, isReady, isServerRendered, metadata, onAdStart, slotId, startFillTimeouts, trackEmpty, trackInitialized];
    $[0] = cancelAdStart;
    $[1] = clearFillTimeouts;
    $[2] = divId;
    $[3] = emailHash;
    $[4] = isReady;
    $[5] = isServerRendered;
    $[6] = metadata;
    $[7] = onAdStart;
    $[8] = slotId;
    $[9] = startFillTimeouts;
    $[10] = trackEmpty;
    $[11] = trackInitialized;
    $[12] = t1;
    $[13] = t2;
  } else {
    t1 = $[12];
    t2 = $[13];
  }
  useLayoutEffect(t1, t2);
  return null;
}